import { type App, computed, type ComputedRef, reactive, type Ref, ref } from 'vue';

import type { MarkSsrError } from '@/../server/server';
import type {
  SitecoreCatalog,
  SitecoreContext,
  SitecoreData,
  SitecorePage,
  SitecoreSettingsSite,
  SitecoreShop,
  SitecoreUser,
} from '@/sitecoreState';
import lowerCaseKeys from '@/utils/lowerCaseKeys';
import parseTokenData, { type IdTokenData } from '@/utils/parseTokenData';

import authorize from './authorize';

/**
 * Sitecore HTML-encodes the query string it hands the rendering host in `state.sitecore.data.query`,
 * so a facet param arrives as `2-Komponenten-Universald&#252;bel`. The browser reads the same URL
 * through `URLSearchParams` and gets the real character, so without this the server puts a string
 * into its GraphQL variables that matches no facet: FactFinder returns 0 hits and the server renders
 * "no products found" for a page that has products.
 */
const namedEntities: Record<string, string> = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: '\u00a0',
  quot: '"',
};

const fromCodePoint = (code: number, fallback: string): string => {
  if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return fallback;
  return String.fromCodePoint(code);
};

export const decodeHtmlEntities = (value: string): string =>
  value.replace(/&(#[Xx][0-9A-Fa-f]+|#\d+|[A-Za-z]+);/g, (match, token: string) => {
    if (token.startsWith('#x') || token.startsWith('#X')) {
      return fromCodePoint(Number.parseInt(token.slice(2), 16), match);
    }
    if (token.startsWith('#')) {
      return fromCodePoint(Number.parseInt(token.slice(1), 10), match);
    }
    return namedEntities[token.toLowerCase()] ?? match;
  });

export const decodeSsrQueryParams = (query: Record<string, string>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(query).map(([key, value]) => [
      decodeHtmlEntities(key),
      typeof value === 'string' ? decodeHtmlEntities(value) : value,
    ]),
  );

export const salesforceItemName = 'a-s-salesforce';

export const categoryFilterExpander = 'CategoryFilterExpand';

const asShopTypeCookie = 'as-shop-type';

export type FischerPluginStore = {
  settings: {
    site: Partial<SitecoreSettingsSite>;
    shop: Partial<SitecoreShop>;
    catalog: Partial<SitecoreCatalog>;
  };
  context: Partial<SitecoreData>;
  sitecoreContext: Partial<SitecoreContext>;
  user: Partial<SitecoreUser>;
  accessTokenRef: Ref<string>;
  idTokenRef: Ref<string>;
  idTokenDataComputed: ComputedRef<IdTokenData | null>;
  dev: {
    markSsrError?: MarkSsrError;
    /** Starts counting reads of a settings branch Sitecore did not send. Server only. */
    armSettingsReporting?: () => void;
  };
  setData(data: any, state: any, markSsrError?: MarkSsrError): void;
};

type fischerPluginPage = {
  url: string;
};

interface AccessPrivilege {
  uid: string;
}

interface AssistedServiceUser {
  uid: string;
  activated: boolean;
  customerId: string;
  deactivationDate: any;
  displayUid: string;
  fanclubnumber: string | null;
  firstName: string;
  fullname: string;
  fullName: string;
  lastName: string;
  lastLogin: string;
  name: string;
  company?: {
    name?: string;
    distributionChannel?: string;
    division?: string;
  };
  orgUnit?: {
    uid?: string;
    preferredOrderUnit?: string | null;
  };
  roles: Array<string>;
  accessPrivileges: Array<AccessPrivilege>;
}

export type SsrCookiesObject = Record<string, string>;

type SsrCookies = null | undefined | SsrCookiesObject;

export type AssistedServiceData = {
  token: string;
  expiresIn: string;
  idToken: string;
  loginSuccessPage: string;
  user: AssistedServiceUser | undefined;
};

// Unfortunately not IE11 compatible, even if we transpile it: import { Base64 } from 'js-base64';
const Base64 = {
  decode: function decode(str: string): string {
    return decodeURIComponent(
      atob(str)
        .split('')
        .map((c) => `%${`00${(c.codePointAt(0) ?? 0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
  },
};

export const assistedServiceItemName = 'AssistedService';

/**
 * FW-11516: a lost assisted service session is only ever noticed on a later page load, by which
 * time nothing says which of the clear paths ran - and they all leave the same empty state. Every
 * one of them therefore leaves a breadcrumb here, and the diagnostics on the account management
 * page report it. sessionStorage: the tab survives the redirect chain, and the trail must not
 * outlive it.
 *
 * Never record the query string: the SSO callback carries `saml_token` in the url, and this trail
 * is written into a toast.
 */
export const assistedServiceClearedItemName = 'AssistedServiceCleared';

export type AssistedServiceClear = {
  reason: string;
  at: string;
  path: string;
};

const CLEAR_TRAIL_LENGTH = 5;

export const readAssistedServiceClears = (): Array<AssistedServiceClear> => {
  try {
    const json = window.sessionStorage.getItem(assistedServiceClearedItemName);
    if (!json) {
      return [];
    }
    const clears = JSON.parse(json);
    return Array.isArray(clears) ? clears : [];
  } catch (_error) {
    return [];
  }
};

const recordAssistedServiceClear = (reason: string) => {
  console.info('Clearing Assisted Service:', reason);
  try {
    const clears = [
      ...readAssistedServiceClears(),
      { reason, at: new Date().toISOString(), path: window.location.pathname },
    ];
    window.sessionStorage.setItem(
      assistedServiceClearedItemName,
      JSON.stringify(clears.slice(-CLEAR_TRAIL_LENGTH)),
    );
  } catch (error) {
    // the breadcrumb is diagnostics only - never let it break the clear itself
    console.debug('Could not record Assisted Service clear', error);
  }
};

const setAssistedService = (
  App: App,
  store: FischerPluginStore,
  assistedService?: AssistedServiceData,
  shopTypeOverride?: string,
  reason?: string,
) => {
  if (IS_SSR) {
    return;
  }
  const expiresIn = assistedService?.expiresIn;
  if (!expiresIn) {
    recordAssistedServiceClear(reason || 'set-without-expiry');
    window.localStorage.removeItem(assistedServiceItemName);
    App.config.globalProperties.$cookies.remove(assistedServiceItemName);
    App.config.globalProperties.$cookies.remove(asShopTypeCookie);
    return;
  }

  // session cookies: Sitecore refreshes the token, so its expiry does not end the session - a
  // failed refresh logs out instead (tokenRefresh.ts)
  const expires = 0;
  App.config.globalProperties.$cookies.set(
    assistedServiceItemName,
    'true',
    expires,
    undefined,
    undefined,
    undefined,
    'Strict',
  );
  const shopType = assistedService.user
    ? shopTypeOverride || store.settings.shop.shopType
    : 'NoShop';
  App.config.globalProperties.$cookies.set(
    asShopTypeCookie,
    shopType,
    expires,
    undefined,
    undefined,
    undefined,
    'Strict',
  );

  window.localStorage.setItem(assistedServiceItemName, JSON.stringify(assistedService));
  // a written session makes every earlier clear history: the trail must only ever describe why
  // *this* session went missing
  try {
    window.sessionStorage.removeItem(assistedServiceClearedItemName);
  } catch (error) {
    console.debug('Could not reset Assisted Service clear trail', error);
  }
};

export type AssistedServiceTokens = Pick<AssistedServiceData, 'token' | 'idToken' | 'expiresIn'>;

const setAssistedServiceTokens = (tokens: AssistedServiceTokens) => {
  if (IS_SSR) {
    return;
  }
  try {
    const stored = window.localStorage.getItem(assistedServiceItemName);
    if (!stored) {
      return;
    }
    window.localStorage.setItem(
      assistedServiceItemName,
      JSON.stringify({ ...JSON.parse(stored), ...tokens }),
    );
  } catch (error) {
    console.error('Could not store the refreshed Assisted Service tokens', error);
  }
};

const clearAssistedService = (App: App, reason: string): null => {
  recordAssistedServiceClear(reason);
  try {
    window.localStorage.removeItem(assistedServiceItemName);
  } catch (error) {
    if (IS_SHOWROOM) {
      console.error(error);
    }
  }
  App.config.globalProperties.$cookies.remove(assistedServiceItemName);
  return null;
};

const isSalesUser = (App: App): boolean => {
  let groups: string[];
  if (IS_SHOWROOM) {
    const assistedService = getAssistedService(App);
    if (!assistedService) {
      return false;
    }
    const tokenData = parseTokenData(assistedService?.idToken);
    groups = tokenData.groups;
  } else {
    groups = App.config.globalProperties.$fischer.user.groups();
  }
  return groups.includes('GROUP-sales-user');
};

/**
 * FW-11516: the assisted service login page mints a session while the visitor is still anonymous -
 * `AgentLogin` establishes the Sitecore session only after this page's html was rendered, so every
 * rendering on it reports no user. The clears that judge the stored session by *this page's* user
 * must stand down here, or a delayed reader - `viewPageEvent` sends its event some 400ms after
 * boot and asks for the session type - wipes the brand new session in the window between
 * `setAssistedService()` and the redirect to the LoginSuccessPage. The next page then has no
 * session, `impersonate()` throws before its fetch, and `Impersonate` is never sent.
 *
 * Matched on the two shapes the login page itself comes back on, so that no other page loosens a
 * check: `saml_token` for the SSO and salesforce callbacks, `state` together with the
 * `asm-login-<state>` cookie only that page writes for the manual agent login.
 */
const isAssistedServiceLoginCallback = (App: App): boolean => {
  const query = new URLSearchParams(window.location.search);
  if (query.get('saml_token')) {
    return true;
  }
  const state = query.get('state');
  return Boolean(state && App.config.globalProperties.$cookies.get(`asm-login-${state}`));
};

/**
 * Assisted service lives in localStorage, so the server never sees a session directly. It learns
 * about one only through forwarded cookies, and only for what has been wired up: `as-shop-type`
 * feeds shopType(). The impersonated identity - customerId(), the token - has no equivalent, so
 * anything keyed by the impersonated customer cannot be derived server-side and will differ between
 * the server markup and the first client render.
 */
const getAssistedService = (App: App): AssistedServiceData | null => {
  if (IS_SSR) {
    return null;
  }
  let valueString: string | undefined | null;
  try {
    valueString = window.localStorage.getItem(assistedServiceItemName);
  } catch (error) {
    console.debug('Could not read Assisted Service from localStorage', error);
  }
  if (!valueString) {
    return null;
  }

  let value: AssistedServiceData | undefined;
  try {
    value = JSON.parse(valueString) as AssistedServiceData;
  } catch (error) {
    console.error('Could not parse AssistedService', error, valueString);
  }
  if (!value) {
    console.info('Assisted Service invalid');
    return clearAssistedService(App, 'unparsable-session');
  }

  const idTokenOriginal = App.config.globalProperties.$fischer.user.idTokenOriginal();

  if (!IS_SHOWROOM) {
    const loginCallback = isAssistedServiceLoginCallback(App);
    const userEmail = App.config.globalProperties.$fischer.user.email();
    if (userEmail === value.user?.uid) {
      if (!idTokenOriginal) {
        // impersonated session
        return value;
      }
      console.info('Assisted Service session replaced by actual user');
      return clearAssistedService(App, 'replaced-by-actual-user');
    }
    if (!idTokenOriginal && !loginCallback) {
      console.info('Session expired, clearing Assisted Service');
      return clearAssistedService(App, 'no-page-id-token');
    }

    const groups = App.config.globalProperties.$fischer.user.groups();
    if (
      !loginCallback &&
      groups &&
      !['asagentgroup', 'customermanagergroup'].every((group) => groups.includes(group))
    ) {
      console.info('Assisted Service no longer active in sitecore');
      return clearAssistedService(App, `page-groups:${groups.join(',') || 'none'}`);
    }
  }

  const isActive = App.config.globalProperties.$cookies.get(assistedServiceItemName) === 'true';
  if (!isActive) {
    console.info('Assisted Service cookie no longer active');
    return clearAssistedService(App, 'cookie-inactive');
  }
  return value;
};

const getSalesforce = (App: App): any => {
  const assistedService = getAssistedService(App);
  if (!assistedService?.user) {
    return null;
  }
  try {
    const salesforceJson = window.sessionStorage.getItem(salesforceItemName) || 'null';
    return JSON.parse(salesforceJson);
  } catch (error) {
    if (IS_SHOWROOM) {
      console.error(error);
    }
    return null;
  }
};

export interface FischerPlugin {
  store: FischerPluginStore;
  settings(): any;
  catalogSettings(): any;
  shopSettings(): any;
  siteSettings(): any;
  cartId(): string;
  setCartId(id: string): void;
  siteLanguage(): string;
  languageIsoCode(): string;
  language_639_1(): string;
  brandId(): string;
  brandName(): string;
  catalogLocale(): string;
  baseSiteId(): string;
  getClientId(): string;
  factFinderChannel(): string;
  useGtins(): boolean;
  isCatalogPage(): boolean;
  isCM(): boolean;
  isProductsPage(): boolean;
  isRootCategoryPage(): boolean;
  isCategoryPage(): boolean;
  isFamilyPage(): boolean;
  isSeriesPage(): boolean;
  isVariantPage(): boolean;
  isSitecoreContent(): boolean;
  catalogId(defaultId?: string): string | null;
  catalogIdPath(): string;
  localRetailers(): boolean;
  hostUrlBase(): string;
  hostname(): string;
  isSecureConnection(): boolean;
  theme(): string;
  lengthUnit(): string;
  shop: {
    shopType(): string;
    isRetailConnect(): boolean;
    isRetailConnectCart(): boolean;
    isOnlineShop(): boolean;
    isClosedShop(): boolean;
    supportsCart(): boolean;
    useEco(): boolean;
    hasAvailabilityStatusOnlyPermission(): boolean;
    hasAvailabilityOnlyGroup(): boolean;
    hasPurchasePermissionWithoutPrices(): boolean;
    activateSalesChannelAssortment(): boolean;
    distributionChannels(): string[];
  };
  pageId(): string;
  pageTemplate(): string;
  uri(): string;
  query(key: string): string | null;
  query(): Record<string, string>;
  hash(key: string): string | null | undefined;
  httpStatus(): number;
  postBody(key: string, noDecode?: boolean): string | null;
  postBody(): Record<string, string>;
  ssrCookies(): SsrCookies;
  gmapApiKey(): string;
  gmapId(): string;
  recaptchaKey(): string;
  cookieBotId(): string;
  enableAnalytics(): boolean;
  isEventHubEnabled(): boolean | undefined;
  isProductSeriesEnabled(): boolean | undefined;
  isLoginDialogEnabled(): boolean;
  getSiteConfig(key: string, defaultValue: any): any;
  isYoukuPlayer(): boolean;
  largeLogo(): boolean;
  constructionEnableThirdPartyProducts(): boolean;
  isChatbotEnabled(): boolean;
  isSupportBoxEnabled(): boolean;
  isProfileFlyoutEnabled(): boolean;
  pages: {
    assistedService(): SitecorePage;
    products(): SitecorePage;
    companies(): SitecorePage;
    registration(): SitecorePage;
    registrationSuccess(): SitecorePage;
    activation(): SitecorePage;
    setPassword(): SitecorePage;
    login(): SitecorePage | fischerPluginPage;
    loginSuccessPage(): SitecorePage | fischerPluginPage;
    logout(): SitecorePage | fischerPluginPage;
    logoutSuccess(): SitecorePage | fischerPluginPage;
    myAccount(): SitecorePage;
    myAccountAddressOverview(): SitecorePage;
    passwordForgotten(): SitecorePage;
    cart(): SitecorePage;
    checkout(): SitecorePage;
    orderHistory(): SitecorePage;
    downloadCenter(): SitecorePage | fischerPluginPage;
    search(): SitecorePage;
    constructionDocumentation(): SitecorePage;
    constructionMonitoring(): SitecorePage;
    constructionThirdPartyOverview(): SitecorePage;
    constructionEJDocuments(): SitecorePage;
  };
  user: {
    getAssistedService(): AssistedServiceData | null;
    getAssistedServiceUser(): any;
    getAssistedServiceEmail(): string | null;
    getSalesforce(): any;
    accessToken(): string;
    setAccessToken(accessToken: string, exp: string): void;
    setIdToken(idToken: string, ttl?: string): void;
    refreshToken(): string;
    setRefreshToken(refreshToken: string): void;
    clearRefreshToken(): void;
    name(): string;
    email(): string;
    customerId(): string;
    distributionChannel(): string | undefined;
    division(): string | undefined;
    idToken(): string;
    idTokenOriginal(): string;
    idTokenData(): IdTokenData | null;
    isIdTokenValid(): boolean;
    groups(): string[];
    preferredOrderUnit(): string | null;
    hasConstructionMonitoringEarlyAccess(): boolean;
    id(): string;
    idOriginal(): string;
    authenticated(): boolean;
    isDiy(): boolean;
    isProfi(): boolean;
    raw(): Partial<SitecoreUser>;
    targetGroup(): string | null;
    subTargetGroup(): unknown | null;
    myfischerStartPageUrl(): string;
    myfischerIsDefaultStartPage(): boolean;
    trackingId(): string;
  };
  app: {
    isAppView(): boolean;
    isUpat(): boolean;
    isFite(): boolean;
    isFiwe(): boolean;
    isEditMode(): boolean;
  };
  authorize(
    anonymous?: boolean,
    authenticated?: boolean,
    groups?: Array<string>,
    membership?: string,
    deny?: boolean,
  ): boolean;
  setAssistedService(value?: AssistedServiceData, shopTypeOverride?: string, reason?: string): void;
  setAssistedServiceTokens(tokens: AssistedServiceTokens): void;
  assistedService(app: App): AssistedServiceData | null;
  // Fails the rendering so Sitecore skips the cache. No-op outside SSR.
  markSsrError(reason: string): void;
  showroomOverrides: any;
}

export const createFischerStore: () => FischerPluginStore = () => {
  const accessTokenRef = ref('');
  const idTokenRef = ref('');
  const idTokenDataComputed = computed<IdTokenData | null>(() =>
    idTokenRef.value ? (parseTokenData(idTokenRef.value) as IdTokenData) : null,
  );

  /**
   * Reporting is armed only once the component itself starts rendering (server.ts arms it right
   * before renderToString). Everything createApp reads while booting - brandId, shop assortment
   * flags, the authenticated user - it reads for every mini-app regardless of what the rendering
   * needs, so counting those would report all four branches on nearly every rendering and drown
   * the signal.
   */
  let armed = false;
  let markSsrErrorRef: MarkSsrError | undefined;
  /** Once per branch: `state.ssrErrors` is concatenated, and one missing option is one fault. */
  const reportedBranches = new Set<SsrSettingsBranch>();
  const reportMissingBranch = (branch: SsrSettingsBranch, key: string) => {
    if (!armed || reportedBranches.has(branch)) {
      return;
    }
    reportedBranches.add(branch);

    const option = SSR_SETTINGS_OPTIONS[branch];
    console.error(
      `Read ${branch}.${key}, but this rendering was not sent it. Tick '${option}' in its SSR ` +
        'options in Sitecore, or it renders against missing data.',
    );
    if (!markSsrErrorRef) {
      console.error('Could not mark ssr error');
      return;
    }
    markSsrErrorRef(`missing ${option}`);
  };

  // NOTE: try to NOT mock anything here, see public/__mocks__/sitecoreApi/api/layout/mockedLayoutData.json
  return {
    // Various settings from Sitecore
    settings: { site: {}, shop: {}, catalog: {} },

    // The actual context from the request
    context: {},

    // The Sitecore context
    sitecoreContext: {},

    // User related data
    user: {},

    accessTokenRef,
    idTokenRef,
    idTokenDataComputed,

    dev: {},

    setData(data: any, state: any, markSsrError?: MarkSsrError): void {
      this.context = state?.sitecore.data || {};
      this.settings = { site: {}, shop: {}, catalog: {}, ...data.sitecore.settings };
      this.sitecoreContext = state.sitecore.context || {};

      /**
       * `data.sitecore.user`, read before the branch below can replace it with a stand-in: the
       * token refs are bootstrap, not a rendering asking for user data.
       */
      const rawUser = data.sitecore.user;

      const reporting =
        REPORT_SSR_DIAGNOSTICS &&
        shouldReportMissingSsrSettings({
          isSsr: IS_SSR,
          isShowroom: IS_SHOWROOM,
          appEnvironment: APP_ENVIRONMENT,
        });
      const branch = (name: SsrSettingsBranch, value: any) =>
        value || (reporting ? createMissingBranchStandIn(name, reportMissingBranch) : {});

      this.user = branch('user', rawUser);
      this.settings.catalog = branch('catalog', this.settings.catalog);
      this.settings.shop = branch('shop', this.settings.shop);
      this.settings.site = branch('site', this.settings.site);

      if (IS_SSR) {
        this.dev.markSsrError = markSsrError;
        // Nothing reads `armed` once the flag is off, so this goes with it.
        if (REPORT_SSR_DIAGNOSTICS) {
          markSsrErrorRef = markSsrError;
          this.dev.armSettingsReporting = () => {
            armed = true;
          };
        }
      }

      if (IS_SHOWROOM) {
        this.context = reactive(this.context);
        this.settings = reactive(this.settings);
        this.sitecoreContext = reactive(this.sitecoreContext);
        this.user = reactive(this.user);
      } else {
        accessTokenRef.value = rawUser?.accessToken || '';
        idTokenRef.value = rawUser?.idToken || '';
      }
    },
  };
};

/**
 * Sitecore still calls the fiwe theme `fide`. Kept separate from the reporting below because a
 * configured theme is never a fault: both `fide` and `fiwe` map to fiwe legitimately.
 */
export const resolveSiteTheme = (siteTheme: string | undefined): string => {
  if (!siteTheme) {
    return 'fiwe';
  }
  return siteTheme === 'fide' ? 'fiwe' : siteTheme;
};

/**
 * The four SSR options a rendering can tick in Sitecore, and the store branch each one fills.
 *
 * Leaving one unticked is correct when the rendering does not need that data, so an absent branch
 * is only a fault once something actually reads from it - which is why these are reported on read
 * rather than on load.
 */
export const SSR_SETTINGS_OPTIONS = {
  site: 'Site Settings',
  catalog: 'Catalog Settings',
  shop: 'Shop Settings',
  user: 'User Data',
} as const;

export type SsrSettingsBranch = keyof typeof SSR_SETTINGS_OPTIONS;

/**
 * Where reporting is worth its noise.
 *
 * Server only: `markSsrError` is handed to the store solely on the SSR path, and a client that
 * received no settings has already failed its layout fetch and never mounts an app.
 *
 * Not in the showroom: it has no Sitecore data at all and fills these branches through its own
 * overrides, so every page would report data that is absent on purpose.
 *
 * TEST and VM only: an SSR error suppresses hydration for that mini-app (see shouldRehydrate), so
 * this changes how a page behaves. UAT stays out along with PROD, to keep the two as close to each
 * other as possible.
 */
export const shouldReportMissingSsrSettings = ({
  isSsr,
  isShowroom,
  appEnvironment,
}: {
  isSsr: boolean;
  isShowroom: boolean;
  appEnvironment: string;
}): boolean => isSsr && !isShowroom && ['TEST', 'VM'].includes(appEnvironment);

/**
 * Keys that are read by machinery rather than by a rendering that wants the data: serialization
 * probes for `toJSON`, `await` probes for `then`, and symbols cover Vue's reactivity markers and
 * `Symbol.toPrimitive`. Reporting on these would blame whichever utility happened to touch the
 * object first.
 */
const NON_READ_KEYS = new Set(['toJSON', 'then', 'constructor', 'hasOwnProperty']);

/**
 * Stands in for a branch Sitecore did not send, and reports the first genuine read of it.
 *
 * Only ever used for an *absent* branch. A branch that arrives empty - `user` for an anonymous
 * visitor, `shop` on a site with no shop - is passed through untouched, so legitimate emptiness is
 * never mistaken for a missing SSR option.
 */
export const createMissingBranchStandIn = (
  branch: SsrSettingsBranch,
  onRead: (branch: SsrSettingsBranch, key: string) => void,
): any =>
  new Proxy(
    {},
    {
      get: (target, key) => {
        if (typeof key === 'symbol' || NON_READ_KEYS.has(key)) {
          return Reflect.get(target, key);
        }
        onRead(branch, key);
        return undefined;
      },
    },
  );

// Vue plugins must export a function named 'install'
function install(App: App, store: FischerPluginStore): void {
  // "standard" convention for Vue plugins to ensure they are only installed once.
  if (App.config.globalProperties.$fischer) {
    return;
  }

  const showroomOverrides: any | null =
    IS_SHOWROOM || (APP_ENVIRONMENT === 'VM' && !IS_SSR)
      ? {
          setContext: (key: string, value: any) => {
            (store.context as Record<string, unknown>)[key] = value;
          },
          setContext2: (key: string, value: any) =>
            ((store.context as Record<string, unknown>)[key] = value),
          setCatalogSetting: (key: string, value: any) => {
            (store.settings.catalog as Record<string, unknown>)[key] = value;
          },
          setShopSetting: (key: string, value: any) => {
            (store.settings.shop as Record<string, unknown>)[key] = value;
          },
          setTheme: (key: string, value: any) => {
            (store.settings.site as Record<string, unknown>)[key] = value;
          },
          setUser: (key: string, value: any) => {
            (store.user as Record<string, unknown>)[key] = value;
          },
          localDistLoginTokenData: () => {
            const idToken = App.config.globalProperties.$cookies.get('IdToken');
            if (!idToken) {
              return {};
            }
            return parseTokenData(idToken);
          },
          async postAceessTokenToVM(url: string) {
            if (IS_SHOWROOM) {
              console.error('only for VM');
              return;
            }
            if (!url) {
              console.error('missing authorize callback url');
              return;
            }
            const [, hash] = url.split('#');
            if (!hash) {
              console.error('missing hash');
              return;
            }
            const search = new URLSearchParams(hash);
            const accessToken = search.get('access_token');
            if (!accessToken) {
              console.error('missing access token');
              return;
            }
            const idToken = search.get('id_token');
            if (!idToken) {
              console.error('missing id token');
              return;
            }

            try {
              const result = await fetch('/webapi/shop/userapi/Login', {
                method: 'POST',
                body: JSON.stringify({
                  User: {
                    AccessToken: accessToken,
                    IdToken: idToken,
                    IsIdsSession: false,
                  },
                }),
                headers: {
                  'Content-Type': 'application/json',
                },
              });
              if (result?.status === 200) {
                console.info('success -> reloading page');
                window.location.reload();
              } else {
                console.error('failed', result?.status);
              }
            } catch (error) {
              console.error(error);
            }
          },
        }
      : null;

  /**
   * URL params are available during SSR: Sitecore hands them over in `state.sitecore.data.query`,
   * which is `store.context`. The exception is a rendering with `Enable SSR Cache` ticked - server.ts
   * drops query and postBody there and anonymises the user, because such a render may be reused.
   * That is what the error below means; it is not a sign that params never arrive.
   *
   * store.context is untyped, so the return types here are what keeps the two query() shapes apart:
   * a miss is null for a key, never a truthy {}.
   */
  const ssrQuery = (): Record<string, string> | undefined => {
    const q = store.context?.query;
    if (!q) {
      console.error('query not in store');
      if (['TEST', 'UAT'].includes(APP_ENVIRONMENT)) {
        if (!store.dev.markSsrError) {
          console.error('Could not mark ssr error', JSON.stringify(store));
        }
        store.dev.markSsrError?.('missing query data');
      }
      return undefined;
    }
    return decodeSsrQueryParams(q);
  };

  const queryParam = (key: string): string | null => {
    if (!IS_SSR) {
      return new URLSearchParams(document.location.search).get(key);
    }
    const q = ssrQuery();
    if (!q || !Object.prototype.hasOwnProperty.call(q, key)) {
      return null;
    }
    return q[key];
  };

  const queryParams = (): Record<string, string> => {
    if (!IS_SSR) {
      const q: Record<string, string> = {};
      new URLSearchParams(document.location.search).forEach((value, searchKey) => {
        q[searchKey] = value;
      });
      return q;
    }
    return ssrQuery() ?? {};
  };

  // overloads need a function declaration; an arrow returning the union is rejected
  function query(key: string): string | null;
  function query(): Record<string, string>;
  function query(key?: string) {
    return key === undefined ? queryParams() : queryParam(key);
  }

  /** Sitecore base64-encodes every value, hence the decode on the way out. */
  function postBody(key: string, noDecode?: boolean): string | null;
  function postBody(): Record<string, string>;
  function postBody(key?: string, noDecode?: boolean) {
    if (IS_SHOWROOM) {
      if (!key) {
        // eslint-disable-next-line no-console
        console.log('postBody without key not mocked');
        return null;
      }
      const value64 = new URLSearchParams(document.location.search).get(key);
      if (value64 === undefined || value64 === null) {
        return null;
      }
      return noDecode ? value64 : Base64.decode(value64);
    }
    const q = store.context.postBody;
    if (!q) {
      return key ? null : {};
    }
    if (!key) {
      return q;
    }
    if (Object.prototype.hasOwnProperty.call(q, key)) {
      const value64 = q[key];
      return noDecode ? value64 : Base64.decode(value64);
    }
    return null;
  }

  const $fischer: FischerPlugin = {
    store,
    // Some convenience methods for accessing important data easily
    settings() {
      return store.settings;
    },
    catalogSettings() {
      return store.settings.catalog;
    },
    shopSettings() {
      return store.settings.shop;
    },
    siteSettings() {
      return store.settings.site;
    },
    cartId(): string {
      const salesforce = getSalesforce(App);
      if (salesforce?.cart?.code) {
        return salesforce?.cart?.code;
      }

      if (IS_SHOWROOM) {
        if (App.config.globalProperties.$cookies.get('CartId')) {
          return App.config.globalProperties.$cookies.get('CartId');
        }
        if (App.config.globalProperties.$cookies.get('AccessToken')) {
          return 'current';
        }
      }
      return store.user.cartId || '';
    },
    async setCartId(id: string) {
      store.user.cartId = id;
      if (IS_SHOWROOM) {
        App.config.globalProperties.$cookies.set(
          'CartId',
          id,
          undefined,
          undefined,
          undefined,
          undefined,
          'Strict',
        );
        return;
      }
      try {
        const response = await fetch('/webapi/shop/cartsapi/setcart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            Cart: {
              cartId: id,
            },
          }),
        });
        if (!response.ok) {
          throw new Error(`Could not set cart: ${response.status}`);
        }
      } catch (error) {
        console.error(error);
      }
    },
    siteLanguage(): string {
      return store.context.language ?? '';
    },
    languageIsoCode(): string {
      const siteLanguage = this.siteLanguage();
      if (siteLanguage === 'da') {
        return 'DK';
      }
      return siteLanguage.split('-')[1];
    },
    language_639_1(): string {
      return this.siteLanguage().split('-')[0];
    },
    brandId(): string {
      if (IS_SHOWROOM) {
        const devBrand = App.config.globalProperties.$cookies.get('devBrand');
        if (devBrand) {
          return devBrand;
        }
      }
      return store.settings.catalog.brandId ?? '';
    },
    brandName(): string {
      if (IS_SHOWROOM && this.brandId() === 'FITE') {
        return 'fischertechnik';
      }
      return store.settings.site.brandName || '';
    },
    catalogLocale(): string {
      return store.settings.catalog.locale ?? '';
    },
    baseSiteId(): string {
      if (IS_SHOWROOM && this.brandId() === 'FITE') {
        return 'FITE-INT';
      }
      return store.settings.shop.baseSiteId ?? '';
    },
    getClientId(): string {
      return (
        this.query('client-id') ||
        store.settings.shop.clientIdOverwrite ||
        this.baseSiteId().toLowerCase()
      );
    },
    factFinderChannel(): string {
      const fromStore = store.settings.site.factFinderChannel;
      if (fromStore) {
        return fromStore;
      }
      const baseSiteId = this.baseSiteId();
      const locale = this.catalogLocale();
      if (!(baseSiteId && locale)) {
        return '';
      }
      const base = baseSiteId.replace(/-.*/, '');
      return `${base}-${locale}`;
    },
    useGtins(): boolean {
      return Boolean(store.settings.shop.useGTIN);
    },
    isCatalogPage(): boolean {
      return Boolean(store.context.isCatalogPage);
    },
    isCM(): boolean {
      return store.context.isContentManagement || false;
    },
    isProductsPage(): boolean {
      return Boolean(store.context.isProductsPage || store.context.isVariantPage);
    },
    isRootCategoryPage(): boolean {
      return Boolean(store.context.isRootCategoryPage);
    },
    isCategoryPage(): boolean {
      if (IS_SHOWROOM && !IS_SSR && window.location.pathname.includes('/product-category')) {
        return true;
      }

      return Boolean(store.context.isCategoryPage);
    },
    isFamilyPage(): boolean {
      return Boolean(store.context.isFamilyPage);
    },
    isSeriesPage(): boolean {
      return Boolean(store.context.isSeriesPage);
    },
    isVariantPage(): boolean {
      return Boolean(store.context.isVariantPage);
    },
    isSitecoreContent(): boolean {
      return Boolean(store.context.isSitecoreContent);
    },
    catalogId(defaultId?: string): string | null {
      if (store.context.catalogId) {
        return store.context.catalogId.toString();
      }
      if (IS_SHOWROOM && defaultId) {
        return defaultId;
      }
      return null;
    },
    catalogIdPath(): string {
      // returns: Standardbefestigungen/Kunsstoffdübel/Duopower - '1001341446/1001341448/1001341458'
      return store.context.catalogIdPath || '';
    },
    localRetailers(): boolean {
      return (
        Boolean(store.settings.site.localRetailers) &&
        !this.shop.hasAvailabilityStatusOnlyPermission()
      );
    },
    hostUrlBase(): string {
      return `http${this.isSecureConnection() ? 's' : ''}://${this.hostname()}`;
    },
    hostname(): string {
      if (IS_SHOWROOM) {
        return window.location.host;
      }
      return store.context.host ?? '';
    },
    isSecureConnection(): boolean {
      if (IS_SHOWROOM) {
        return window.location.protocol === 'https:';
      }
      return Boolean(store.context.isSecureConnection);
    },
    theme(): string {
      return resolveSiteTheme(store.settings?.site?.theme);
    },
    lengthUnit(): string {
      return store.settings.site.lengthUnit || 'km';
    },
    shop: {
      shopType(): string {
        if (isSalesUser(App)) {
          if (IS_SSR) {
            return store.context.cookies?.[asShopTypeCookie] || 'NoShop';
          }
          if (!getAssistedService(App)?.user) {
            return 'NoShop';
          }
        }
        return store.settings.shop.shopType || 'NoShop';
      },
      isRetailConnect(): boolean {
        return (
          this.shopType() === 'RetailConnect' &&
          !this.hasAvailabilityOnlyGroup() &&
          !this.hasPurchasePermissionWithoutPrices()
        );
      },
      isRetailConnectCart(): boolean {
        return (
          this.shopType() === 'RetailConnectCart' &&
          !this.hasAvailabilityOnlyGroup() &&
          !this.hasPurchasePermissionWithoutPrices()
        );
      },
      isOnlineShop(): boolean {
        return this.shopType() === 'OnlineShop' || this.hasPurchasePermissionWithoutPrices();
      },
      isClosedShop(): boolean {
        return this.shopType() === 'ClosedOnlineShop' || this.hasPurchasePermissionWithoutPrices();
      },
      supportsCart(): boolean {
        return ['ClosedOnlineShop', 'RetailConnectCart', 'OnlineShop'].includes(this.shopType());
      },
      useEco(): boolean {
        return store.context.language === 'fr-FR';
      },
      hasAvailabilityStatusOnlyPermission(): boolean {
        if (this.isOnlineShop() || this.isClosedShop()) {
          return false;
        }
        return this.hasAvailabilityOnlyGroup();
      },
      hasAvailabilityOnlyGroup(): boolean {
        const groups = App.config.globalProperties.$fischer.user.groups();

        if (!groups || !Array.isArray(groups) || !groups.length) {
          return false;
        }

        return groups.includes('ACCESS-PRIVILEGE-SHOW-AVAILABILITY');
      },
      hasPurchasePermissionWithoutPrices(): boolean {
        const groups = App.config.globalProperties.$fischer.user.groups();

        if (!groups || !Array.isArray(groups) || !groups.length) {
          return false;
        }

        if (groups.includes(`${store.settings.shop.baseSiteId}-shop`)) {
          return false;
        }

        return groups.includes('ACCESS-PRIVILEGE-PURCHASING-WITHOUT-PRICES');
      },
      activateSalesChannelAssortment(): boolean {
        if (IS_SHOWROOM) {
          return App.config.globalProperties.$fischer.baseSiteId() === 'FIWE-DE';
        }
        return Boolean(store.settings.shop.activateSalesChannelAssortment);
      },
      // Prefer getSalesChannels() from @/composables/salesChannelFilter — it gates on activateSalesChannelAssortment.
      distributionChannels(): string[] {
        const userChannel = App.config.globalProperties.$fischer.user.distributionChannel();
        if (userChannel) {
          return [userChannel];
        }
        if (IS_SHOWROOM) {
          const baseSiteId = App.config.globalProperties.$fischer.baseSiteId();
          if (baseSiteId === 'FIWE-DE') {
            return ['10', '20', '90'];
          }
        }
        return (
          store.settings.shop.distributionChannels?.filter(
            (channel: string | null | undefined) => channel !== null && channel !== undefined,
          ) || []
        );
      },
    },
    pageId(): string {
      return store.context.pageId?.replace('{', '').replace('}', '') ?? '';
    },
    pageTemplate(): string {
      return store.context.pageTemplate || '';
    },
    uri(): string {
      if (IS_SHOWROOM) {
        return window.location.pathname;
      }
      return store.context.uri ?? '';
    },
    query,
    hash(key: string) {
      if (!IS_SSR && key) {
        const hashes = window.location.hash.slice(1);
        return new URLSearchParams(hashes).get(key);
      }

      return null;
    },
    httpStatus(): number {
      return store.context.httpStatus || 0;
    },
    postBody,
    // you probably do not want to call this directly
    // use composable/ssrCookies instead
    ssrCookies(): SsrCookies {
      return store.context.cookies;
    },
    gmapApiKey(): string {
      return store.settings.site.googleMapsApiKey || '';
    },
    gmapId(): string {
      return store.settings.site.googleMapsStyleId || '';
    },
    recaptchaKey(): string {
      return store.settings.site.recaptchaKey || '';
    },
    cookieBotId(): string {
      return store.settings.site.cookieBotId || '';
    },
    enableAnalytics(): boolean {
      if (IS_SSR) {
        return false;
      }

      if (IS_SHOWROOM) {
        return true;
      }
      return store.settings.site.enableAnalytics ?? false;
    },
    isEventHubEnabled(): boolean | undefined {
      return store.settings.site.enableEventHub;
    },
    isProductSeriesEnabled(): boolean | undefined {
      if (IS_SHOWROOM && App.config.globalProperties.$fischer.baseSiteId() === 'FIWE-DE') {
        return true;
      }
      return store.settings.site.activateProductSeries ?? false;
    },
    isLoginDialogEnabled(): boolean {
      return Boolean(
        store.settings.site.enableLocalSSOLayer && !App.config.globalProperties.isAppView,
      );
    },
    getSiteConfig(key: string, defaultValue: any) {
      const value = (store.settings.site as Record<string, unknown>)[key];
      if (value !== undefined) {
        return value;
      }
      return defaultValue;
    },
    isYoukuPlayer(): boolean {
      return store.settings.site.youku ?? false;
    },
    largeLogo(): boolean {
      return store.settings.site.largeLogo ?? false;
    },
    constructionEnableThirdPartyProducts(): boolean {
      return store.settings.site.constructionEnableThirdPartyProducts ?? false;
    },
    isChatbotEnabled(): boolean {
      return store.settings.site.enableChatBot ?? false;
    },
    isSupportBoxEnabled(): boolean {
      return store.settings.site.enableSupportBox ?? false;
    },
    isProfileFlyoutEnabled(): boolean {
      return Boolean(
        store.settings.site.enableProfileFlyout && !App.config.globalProperties.isAppView,
      );
    },
    pages: {
      assistedService(): SitecorePage {
        return lowerCaseKeys(store.settings.site.adminLoginPage || {});
      },
      products(): SitecorePage {
        return lowerCaseKeys(store.settings.site.productsPage || {});
      },
      companies(): SitecorePage {
        return lowerCaseKeys(store.settings.site.companyPage || {});
      },
      registration(): SitecorePage {
        return lowerCaseKeys(store.settings.site.registrationPage || {});
      },
      registrationSuccess(): SitecorePage {
        return lowerCaseKeys(store.settings.site.registrationSuccessPage || {});
      },
      activation(): SitecorePage {
        // The siblings all fall back to {}; this one used to hand out undefined.
        return store.settings.site.activationPage ?? ({} as SitecorePage);
      },
      setPassword(): SitecorePage {
        return lowerCaseKeys(store.settings.site.setPasswordPage || {});
      },
      login(): SitecorePage {
        return lowerCaseKeys(store.settings.site.loginPage || {});
      },
      loginSuccessPage(): SitecorePage | fischerPluginPage {
        const assistedService = getAssistedService(App);
        if (assistedService && !assistedService.user) {
          return {
            url: assistedService.loginSuccessPage,
          };
        }
        return lowerCaseKeys(store.settings.site.loginSuccessPage || {});
      },
      logout(): SitecorePage | fischerPluginPage {
        return lowerCaseKeys(store.settings.site.logoutPage || {});
      },
      logoutSuccess(): SitecorePage | fischerPluginPage {
        if (IS_SHOWROOM) {
          return { url: '/my-account/logout-success' };
        }
        return lowerCaseKeys(store.settings.site.logoutSuccessPage || {});
      },
      myAccount(): SitecorePage {
        return lowerCaseKeys(store.settings.site.myAccountPage || {});
      },
      myAccountAddressOverview(): SitecorePage {
        return lowerCaseKeys(store.settings.site.myAccountAddressOverviewPage || {});
      },
      passwordForgotten(): SitecorePage {
        return lowerCaseKeys(store.settings.site.passwordForgottenPage || {});
      },
      cart(): SitecorePage {
        return lowerCaseKeys(store.settings.site.cartPage || {});
      },
      checkout(): SitecorePage {
        return lowerCaseKeys(store.settings.site.checkoutPage || {});
      },
      orderHistory(): SitecorePage {
        return lowerCaseKeys(store.settings.site.orderHistoryPage || {});
      },
      downloadCenter(): SitecorePage | fischerPluginPage {
        if (IS_SHOWROOM) {
          return { url: '/download-center-fi' };
        }
        return lowerCaseKeys(store.settings.site.downloadCenterPage || {});
      },
      search(): SitecorePage {
        return lowerCaseKeys(store.settings.site.searchPage || {});
      },
      constructionDocumentation(): SitecorePage {
        return lowerCaseKeys(store.settings.site.constructionDocumentationPage || {});
      },
      constructionMonitoring(): SitecorePage {
        return lowerCaseKeys(store.settings.site.constructionMonitoringPage || {});
      },
      constructionThirdPartyOverview(): SitecorePage {
        return lowerCaseKeys(store.settings.site.constructionThirdPartyPage || {});
      },
      constructionEJDocuments(): SitecorePage {
        return lowerCaseKeys(store.settings.site.constructionEJDocumentsPage || {});
      },
    },
    user: {
      getAssistedService(): AssistedServiceData | null {
        const assistedService = getAssistedService(App);
        if (!assistedService) {
          return null;
        }
        if (!assistedService.token) {
          setAssistedService(App, store, undefined, undefined, 'session-without-token');
          return null;
        }
        // an anonymous page user is expected while the agent is logging in on this page load
        if (!this.authenticated() && !isAssistedServiceLoginCallback(App)) {
          // session expired
          setAssistedService(App, store, undefined, undefined, 'page-not-authenticated');
          return null;
        }
        return assistedService;
      },
      getAssistedServiceUser(): any {
        const assistedService = this.getAssistedService();
        if (!assistedService) {
          return null;
        }
        return parseTokenData(assistedService.idToken);
      },
      getAssistedServiceEmail(): string | null {
        const assistedService = this.getAssistedService();
        if (assistedService?.user) {
          return assistedService.user.uid;
        }

        return null;
      },
      getSalesforce(): any {
        return getSalesforce(App);
      },
      accessToken(): string {
        const assistedService = this.getAssistedService();
        if (assistedService?.user) {
          return assistedService.token;
        }

        if (IS_SHOWROOM) {
          if (App.config.globalProperties.$cookies.get('AccessToken')) {
            return App.config.globalProperties.$cookies.get('AccessToken');
          }
        }

        return store.user.accessToken || '';
      },
      setAccessToken(accessToken: string, exp: string): void {
        if (IS_SHOWROOM) {
          App.config.globalProperties.$cookies.set(
            'AccessToken',
            accessToken,
            exp,
            undefined,
            undefined,
            undefined,
            'Strict',
          );
        } else {
          store.user.accessToken = accessToken;
        }
        store.accessTokenRef.value = accessToken;
      },
      setIdToken(idToken: string, ttl?: string): void {
        if (IS_SHOWROOM) {
          if (ttl) {
            App.config.globalProperties.$cookies.set(
              'IdToken',
              idToken,
              ttl,
              undefined,
              undefined,
              undefined,
              'Strict',
            );
          }
        } else {
          store.user.idToken = idToken;
        }
        store.idTokenRef.value = idToken;
      },
      refreshToken(): string {
        return App.config.globalProperties.$cookies.get('RefreshToken') || '';
      },
      setRefreshToken(refreshToken: string): void {
        // Use '1d' TTL — the auth server is authoritative on actual expiry
        App.config.globalProperties.$cookies.set(
          'RefreshToken',
          refreshToken,
          '1d',
          undefined,
          undefined,
          undefined,
          'Strict',
        );
      },
      clearRefreshToken(): void {
        App.config.globalProperties.$cookies.remove('RefreshToken');
      },
      name(): string {
        const assistedService = this.getAssistedService();
        if (assistedService?.user) {
          return assistedService.user.name;
        }
        const idToken = this.idTokenData();
        return idToken?.name || '';
      },
      email(): string {
        if (IS_SHOWROOM) {
          return showroomOverrides.localDistLoginTokenData().email;
        }

        return store.user.email || '';
      },
      customerId(): string {
        const assistedService = this.getAssistedService();
        if (assistedService?.user) {
          return assistedService.user.customerId;
        }

        if (IS_SHOWROOM) {
          return showroomOverrides.localDistLoginTokenData().sapCustomerId;
        }

        return store.user.customerId || '';
      },
      distributionChannel(): string | undefined {
        const assistedService = this.getAssistedService();
        if (assistedService) {
          const salesforceChannel = this.getSalesforce()?.distributionChannel;
          if (salesforceChannel) {
            return salesforceChannel;
          }
          if (assistedService.user) {
            return assistedService.user.company?.distributionChannel;
          }
        }
        const idToken = this.idTokenData();
        return idToken?.distributionChannel;
      },
      division(): string | undefined {
        const assistedService = this.getAssistedService();
        if (assistedService) {
          const salesforceDivision = this.getSalesforce()?.division;
          if (salesforceDivision) {
            return salesforceDivision;
          }

          if (assistedService.user) {
            return assistedService.user.company?.division;
          }
        }
        const idToken = this.idTokenData();
        return idToken?.division;
      },
      idToken(): string {
        const assistedService = this.getAssistedService();
        if (assistedService?.user) {
          return assistedService.idToken;
        }

        return this.idTokenOriginal();
      },
      idTokenOriginal(): string {
        if (IS_SHOWROOM) {
          const idToken = App.config.globalProperties.$cookies.get('IdToken');
          if (idToken) {
            return idToken;
          }
        }
        return store.user.idToken || '';
      },
      idTokenData(): IdTokenData | null {
        const assistedService = this.getAssistedService();
        if (assistedService?.user) {
          return parseTokenData(assistedService.idToken) as IdTokenData;
        }
        if (IS_SHOWROOM && !store.idTokenRef.value) {
          const cookieIdToken = App.config.globalProperties.$cookies.get('IdToken');
          if (cookieIdToken) {
            return parseTokenData(cookieIdToken);
          }
        }
        return store.idTokenDataComputed.value;
      },
      isIdTokenValid(): boolean {
        const exp = this.idTokenData()?.exp;
        return Boolean(exp && exp * 1000 < Date.now());
      },
      groups(): string[] {
        if (IS_SHOWROOM) {
          const assistedService = this.getAssistedService();
          if (assistedService?.user) {
            return [
              ...assistedService.user.accessPrivileges.map((acl: any) => acl.uid),
              ...assistedService.user.roles,
            ];
          }
          if (this.authenticated()) {
            const tokenData = showroomOverrides.localDistLoginTokenData();
            if (tokenData?.groups) {
              return tokenData.groups;
            }
          }
        }
        return store.user.groups || [];
      },
      preferredOrderUnit(): string | null {
        const assistedService = this.getAssistedService();
        if (assistedService?.user) {
          return assistedService.user.orgUnit?.preferredOrderUnit || null;
        }
        return store.user.preferredOrderUnit || null;
      },
      hasConstructionMonitoringEarlyAccess(): boolean {
        if (IS_SHOWROOM) {
          return true;
        }

        return this.groups().includes('ACCESS-PRIVILEGE-CONSTR-MONITORING-EARLY-ACCESS');
      },
      id(): string {
        const id = this.idOriginal();
        if (!id) {
          return 'anonymous';
        }

        const assistedService = this.getAssistedService();
        if (assistedService?.user) {
          return assistedService.user.uid;
        }

        return id;
      },
      idOriginal(): string {
        if (IS_SHOWROOM) {
          if (this.authenticated()) {
            return showroomOverrides.localDistLoginTokenData().sub;
          }
          return 'anonymous';
        }
        return store.user.userId || '';
      },
      authenticated(): boolean {
        if (IS_SHOWROOM) {
          if (App.config.globalProperties.$cookies.get('AccessToken')) {
            return true;
          }
          return false;
        }
        return Boolean(store.user.authenticated);
      },
      isDiy(): boolean {
        return Boolean(store.user.isDiy);
      },
      isProfi(): boolean {
        return Boolean(store.user.isProfi);
      },
      raw(): Partial<SitecoreUser> {
        return store.user;
      },
      targetGroup(): string | null {
        return store.user.targetGroup || null;
      },
      subTargetGroup(): unknown | null {
        return store.user.subTargetGroup || null;
      },
      myfischerStartPageUrl(): string {
        if (IS_SHOWROOM) {
          return '/my-account/dashboard';
        }
        return store.user.startPageUrl || '';
      },
      myfischerIsDefaultStartPage(): boolean {
        return Boolean(store.user.isDefaultStartPage);
      },
      trackingId(): string {
        return store.user.trackingId || '';
      },
    },
    app: {
      isAppView(): boolean {
        return App.config.globalProperties.isAppView;
      },
      isUpat(): boolean {
        return App.config.globalProperties.isUpat;
      },
      isFite(): boolean {
        return App.config.globalProperties.isFite;
      },
      isFiwe(): boolean {
        return !this.isFite() && !this.isUpat();
      },
      isEditMode(): boolean {
        return App.config.globalProperties.isEditMode;
      },
    },
    authorize,
    setAssistedService: (value?: AssistedServiceData, shopTypeOverride?: string, reason?: string) =>
      setAssistedService(App, store, value, shopTypeOverride, reason),
    setAssistedServiceTokens,
    assistedService: () => getAssistedService(App),
    markSsrError: (reason: string) => store.dev.markSsrError?.(reason),
    showroomOverrides,
  };

  App.config.globalProperties.$fischer = $fischer;
}

export default { install };
